"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const itertools_1 = require("./util/itertools");
/**
 * Transforms feature vectors to vectors of certain monomials thereof.
 *
 * E.g. after setting options (degree, homogeneous, interactionOnly), and
 * *fit()*ing, *transform()*s lists of feature vectors like [a, b, c], where a,
 * b and c are numbers, into the following possible outputs
 *
 * - [a^2, ba, ca, a, b^2, cb, b, c^2, c, 1] (degree = 2), that is, all
 *   monomials in a, b, c up to degree two.
 *
 * - [a^2, ba, ca, b^2, cb, c^2] (degree = 2, homogeneous = true), that is, all
 *   monomials in a, b, c of degree exactly equal to two.
 *
 * - [ab, ac, bc] (degree = 2, homogeneous = true, interactionOnly = true), that
 *   is, all monomials in a, b, c of degree exactly equal to two and no feature
 *   raised to a power larger than one.
 *
 * - [ab, ac, a, bc, b, c, 1] (degree = 2, homogeneous = false, interactionOnly
 *   = true), that is, all monomials in a, b, c of degree at most two and no
 *   feature raised to a power larger than one.
 *
 * Of course, the number of features is not restricted to three, but must be the
 * same in each feature vector of the list to be *transformed()*.
 */
class PolynomialFeatures {
    /**
     * Basic configuration. You can skip configuration by providing no arguments.
     *
     * In case no arguments are given you have to use the method fromConfig(...)
     * to set up a configuration in order to use the methods fit(...),
     * transform(...) and fitTransform(...).
     *
     * @param degree Highest order of the monomials
     * @param homogeneous Whether to include only highest order monomials
     * @param interactionOnly Whether to disallow higher powers of single features
     */
    constructor(degree, homogeneous = false, interactionOnly = false) {
        if (degree !== undefined) {
            this._degree = degree;
            this._homogeneous = homogeneous;
            this._combinations = interactionOnly ? itertools_1.combinations
                : itertools_1.combinationsWithRepitition;
        }
    }
    /** Configuration option 'degree' */
    get degree() {
        return this._degree;
    }
    /** Configuration option 'homogenous' */
    get homogenous() {
        return this._homogeneous;
    }
    /** Configuration option 'interactionOnly' */
    get interactionOnly() {
        return (this._combinations === itertools_1.combinations);
    }
    /**
     * Number of input features.
     *
     * The only configuration option which is set by the fit method.
     */
    get nFeaturesIn() {
        return this._nFeaturesIn;
    }
    /** Saves configuration to a simple option-bag.
     *
     * The configuration specifies the internal state of PolynomialFeatures
     * completely. Hence the config of the transformer can be used to save it to a
     * file.
    */
    get config() {
        return { degree: this.degree, homogeneous: this.homogenous,
            interactionOnly: this.interactionOnly, nFeaturesIn: this.nFeaturesIn };
    }
    /** Loads configuration from simple option-bag. */
    fromConfig(config) {
        this._degree = config.degree;
        this._homogeneous = config.homogeneous;
        this._combinations = config.interactionOnly ? itertools_1.combinations
            : itertools_1.combinationsWithRepitition;
        this._nFeaturesIn = config.nFeaturesIn;
    }
    /** Sets the number of input features.
     *
     * Call this method before transform(...). Afterwards the transform(...)
     * method will only accept input with the correct number of features.
    */
    fit(x) {
        this._nFeaturesIn = x[0].length;
    }
    /**
     * Returns the list of polynomial features corresponding to x.
     *
     * @param x List of features vectors (with same number of features each).
     */
    transform(x) {
        let y = [];
        for (let xi of x) {
            if (xi.length !== this.nFeaturesIn) {
                let message = `Invalid input: Input dimension is ${xi.length} expected ${this.nFeaturesIn}.`;
                if (!this.nFeaturesIn)
                    message += ' Maybe you forgot to fit(...) the data.';
                throw Error(message);
            }
            let yi = [];
            const ximod = this._homogeneous ? xi : xi.concat([1]);
            for (let comb of this._combinations(ximod, this._degree)) {
                yi.push(comb.reduce((p, c) => p *= c, 1));
            }
            if (this.interactionOnly && !this._homogeneous)
                yi.push(1);
            y.push(yi);
        }
        return y;
    }
    /**
     * Same as applying first fit(...) and then transform(...) to x.
     *
     * @param x List of features vectors.
     */
    fitTransform(x) {
        this.fit(x);
        return this.transform(x);
    }
}
exports.PolynomialFeatures = PolynomialFeatures;
